/**
 * Page profil formateur — affichage + upload photo.
 */
import React, { useState, useRef, useEffect } from 'react';
import { UserCircle, Camera, Trash2, Loader2, X, Bell, BellOff, Send, KeyRound, Eye, EyeOff, ShieldCheck, User, Phone, MapPin, Cake, Droplet, GraduationCap, PhoneCall, CheckCircle2, Save } from 'lucide-react';
import { FormateurLayout } from '../FormateurLayout';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../store';
import { patchUser } from '../../store/slices/authSlice';
import {
  useFormateurMeQuery, useUploaderMaPhotoMutation, useSupprimerMaPhotoMutation,
  usePushTestFormateurMutation,
} from '../../services/formateurApi';
import { usePushFormateur } from '../hooks/usePushFormateur';

export default function Profil() {
  const { data } = useFormateurMeQuery();
  const user = useSelector((s: RootState) => s.auth.user) as any;
  const dispatch = useDispatch();
  const photoUrl = user?.photo_url || null;
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploader, { isLoading: uploading }] = useUploaderMaPhotoMutation();
  const [supprimer, { isLoading: deleting }] = useSupprimerMaPhotoMutation();
  const [preview, setPreview] = useState<string | null>(null);
  const [erreur, setErreur] = useState('');

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setErreur('');
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setErreur('Photo trop volumineuse (max 5 Mo).');
      return;
    }
    const blobUrl = URL.createObjectURL(f);
    setPreview(blobUrl);
    const fd = new FormData();
    fd.append('photo', f);
    try {
      const res = await uploader(fd).unwrap();
      // Patch local Redux + localStorage → l'avatar dans l'accueil suit
      dispatch(patchUser({ photo_url: res.photo_url || blobUrl }));
      // Garde le preview affiché (sera remplacé par photoUrl au prochain render)
    } catch (err: any) {
      setErreur(err?.data?.detail || 'Erreur upload');
      setPreview(null);
      URL.revokeObjectURL(blobUrl);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer votre photo ?')) return;
    try {
      await supprimer().unwrap();
      dispatch(patchUser({ photo_url: null }));
      setPreview(null);
    } catch {
      setErreur('Erreur lors de la suppression');
    }
  };

  return (
    <FormateurLayout title="Mon profil">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-3 text-center">
        <div className="relative inline-block">
          {(preview || photoUrl) ? (
            <img src={preview || photoUrl!} alt="Profil"
              className="w-28 h-28 rounded-full object-cover ring-2 ring-amber-500/50" />
          ) : (
            <div className="w-28 h-28 rounded-full bg-gray-800 ring-2 ring-amber-500/30 flex items-center justify-center text-gray-500">
              <UserCircle size={64} />
            </div>
          )}
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-amber-600 hover:bg-amber-700 text-white flex items-center justify-center shadow-lg">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />

        <h1 className="text-lg font-bold text-white mt-3">{data?.formateur.nom_complet || '...'}</h1>
        {data?.formateur.email && (
          <div className="text-xs text-gray-400 mt-0.5">{data.formateur.email}</div>
        )}
        <div className="text-[10px] uppercase tracking-wider text-amber-300 mt-1">
          {data?.formateur.role || ''}
        </div>

        {photoUrl && !preview && (
          <button onClick={handleDelete} disabled={deleting}
            className="mt-3 inline-flex items-center gap-1 text-[11px] text-red-400 hover:text-red-300 px-2 py-1 hover:bg-red-900/20 rounded">
            <Trash2 size={11} /> Supprimer la photo
          </button>
        )}

        {erreur && (
          <div className="mt-3 bg-red-900/30 border border-red-800 text-red-200 rounded p-2 text-xs">
            {erreur}
          </div>
        )}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 mb-3">
        <h2 className="text-[11px] uppercase tracking-wider text-gray-400 font-bold mb-2">
          Statistiques
        </h2>
        <div className="grid grid-cols-3 gap-2 text-center">
          <Stat label="Matières" v={data?.kpi.nb_matieres ?? 0} />
          <Stat label="Classes" v={data?.kpi.nb_classes ?? 0} />
          <Stat label="Non lus" v={data?.kpi.nb_messages_non_lus ?? 0} />
        </div>
      </div>

      <PersonalInfoCard />
      <PushCard />
      <ChangePasswordCard />
    </FormateurLayout>
  );
}

/**
 * Carte "Mes informations personnelles" — le formateur complète lui-même son
 * téléphone, adresse, groupe sanguin, date d'anniversaire, contact d'urgence…
 *
 * Ces infos sont ensuite consultables par la scolarité (annuaire des contacts
 * formateurs). L'année de naissance est optionnelle : le formateur peut ne
 * renseigner que jour+mois pour être discret sur son âge tout en permettant
 * de lui souhaiter son anniversaire.
 */
function PersonalInfoCard() {
  type ProfilPerso = {
    id?: number;
    nom?: string;
    prenoms?: string;
    email?: string;
    telephone?: string;
    telephone_secondaire?: string;
    specialite?: string;
    diplome?: string;
    adresse?: string;
    ville?: string;
    groupe_sanguin?: string;
    sexe?: string;
    jour_naissance?: number | null;
    mois_naissance?: number | null;
    annee_naissance?: number | null;
    contact_urgence_nom?: string;
    contact_urgence_telephone?: string;
    date_modification_profil?: string | null;
  };
  const [profil, setProfil] = useState<ProfilPerso | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [expanded, setExpanded] = useState(false);

  const authFetch = async (input: string, init?: RequestInit) => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
    return fetch(input, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers || {}),
      },
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const resp = await authFetch('/api/portail-formateur/me/profil-personnel/');
        const data = await resp.json().catch(() => ({} as ProfilPerso));
        if (resp.ok) setProfil(data);
      } catch { /* noop */ }
      finally { setLoading(false); }
    })();
  }, []);

  const setField = (k: keyof ProfilPerso, v: any) => setProfil((p) => ({ ...(p || {}), [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setSaving(true);
    try {
      const resp = await authFetch('/api/portail-formateur/me/profil-personnel/', {
        method: 'PUT',
        body: JSON.stringify(profil || {}),
      });
      const data = await resp.json().catch(() => ({} as any));
      if (!resp.ok) {
        setMessage({ type: 'error', text: data.detail || 'Erreur lors de l\'enregistrement.' });
      } else {
        setProfil(data);
        setMessage({ type: 'success', text: 'Profil mis à jour.' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Erreur réseau.' });
    } finally {
      setSaving(false);
    }
  };

  const missingHint = () => {
    const missing = [];
    if (!profil?.telephone) missing.push('Téléphone');
    if (!profil?.adresse) missing.push('Adresse');
    if (!profil?.jour_naissance) missing.push('Date de naissance');
    if (!profil?.contact_urgence_telephone) missing.push('Contact d\'urgence');
    return missing;
  };
  const missing = missingHint();

  const jours = Array.from({ length: 31 }, (_, i) => i + 1);
  const mois = [
    { v: 1, l: 'Janvier' }, { v: 2, l: 'Février' }, { v: 3, l: 'Mars' },
    { v: 4, l: 'Avril' }, { v: 5, l: 'Mai' }, { v: 6, l: 'Juin' },
    { v: 7, l: 'Juillet' }, { v: 8, l: 'Août' }, { v: 9, l: 'Septembre' },
    { v: 10, l: 'Octobre' }, { v: 11, l: 'Novembre' }, { v: 12, l: 'Décembre' },
  ];
  const currentYear = new Date().getFullYear();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <User size={18} className="text-indigo-600 dark:text-indigo-400" />
            Mes informations personnelles
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Ces informations facilitent le contact par la scolarité. L'année de naissance est optionnelle.
          </p>
        </div>
        {!expanded && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors flex-shrink-0"
          >
            Modifier
          </button>
        )}
      </div>

      {!expanded && missing.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2 text-xs text-amber-800 dark:text-amber-200 mb-3">
          À compléter : {missing.join(' · ')}
        </div>
      )}

      {loading ? (
        <div className="text-center py-4 text-gray-400 text-sm">
          <Loader2 size={16} className="inline-block animate-spin mr-1" /> Chargement…
        </div>
      ) : !expanded ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <InfoRow icon={<Phone size={13} />} label="Téléphone" value={profil?.telephone || '—'} />
          <InfoRow icon={<PhoneCall size={13} />} label="Alt" value={profil?.telephone_secondaire || '—'} />
          <InfoRow icon={<MapPin size={13} />} label="Adresse" value={[profil?.adresse, profil?.ville].filter(Boolean).join(', ') || '—'} />
          <InfoRow icon={<Cake size={13} />} label="Anniversaire" value={
            profil?.jour_naissance && profil?.mois_naissance
              ? `${String(profil.jour_naissance).padStart(2, '0')}/${String(profil.mois_naissance).padStart(2, '0')}${profil.annee_naissance ? '/' + profil.annee_naissance : ''}`
              : '—'
          } />
          <InfoRow icon={<Droplet size={13} />} label="Groupe sanguin" value={profil?.groupe_sanguin || '—'} />
          <InfoRow icon={<GraduationCap size={13} />} label="Diplôme" value={profil?.diplome || '—'} />
          <InfoRow icon={<UserCircle size={13} />} label="Urgence" value={
            [profil?.contact_urgence_nom, profil?.contact_urgence_telephone].filter(Boolean).join(' · ') || '—'
          } className="md:col-span-2" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Field label="Téléphone principal *" icon={<Phone size={13} />}>
              <input type="tel" value={profil?.telephone || ''} onChange={(e) => setField('telephone', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </Field>
            <Field label="Téléphone alternatif" icon={<PhoneCall size={13} />}>
              <input type="tel" value={profil?.telephone_secondaire || ''} onChange={(e) => setField('telephone_secondaire', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </Field>
            <Field label="Email">
              <input type="email" value={profil?.email || ''} onChange={(e) => setField('email', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </Field>
            <Field label="Ville">
              <input type="text" value={profil?.ville || ''} onChange={(e) => setField('ville', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </Field>
            <Field label="Adresse" icon={<MapPin size={13} />} className="md:col-span-2">
              <input type="text" value={profil?.adresse || ''} onChange={(e) => setField('adresse', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </Field>
            <Field label="Groupe sanguin" icon={<Droplet size={13} />}>
              <select value={profil?.groupe_sanguin || ''} onChange={(e) => setField('groupe_sanguin', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">— Non renseigné —</option>
                {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Sexe">
              <select value={profil?.sexe || ''} onChange={(e) => setField('sexe', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">— Non renseigné —</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
            </Field>
          </div>

          {/* Date d'anniversaire — année optionnelle */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5">
              <Cake size={13} /> Date de naissance <span className="text-gray-400 font-normal">(année optionnelle — pour la discrétion)</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <select value={profil?.jour_naissance || ''} onChange={(e) => setField('jour_naissance', e.target.value ? Number(e.target.value) : null)}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">— Jour —</option>
                {jours.map(j => <option key={j} value={j}>{j}</option>)}
              </select>
              <select value={profil?.mois_naissance || ''} onChange={(e) => setField('mois_naissance', e.target.value ? Number(e.target.value) : null)}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">— Mois —</option>
                {mois.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select>
              <input
                type="number"
                min={1940} max={currentYear} step={1}
                placeholder="Année (facultatif)"
                value={profil?.annee_naissance || ''}
                onChange={(e) => setField('annee_naissance', e.target.value ? Number(e.target.value) : null)}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          {/* Contact urgence */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <Field label="Contact urgence — nom">
              <input type="text" value={profil?.contact_urgence_nom || ''} onChange={(e) => setField('contact_urgence_nom', e.target.value)}
                placeholder="Ex : Conjoint, parent, ami…"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </Field>
            <Field label="Contact urgence — téléphone">
              <input type="tel" value={profil?.contact_urgence_telephone || ''} onChange={(e) => setField('contact_urgence_telephone', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </Field>
            <Field label="Spécialité">
              <input type="text" value={profil?.specialite || ''} onChange={(e) => setField('specialite', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </Field>
            <Field label="Diplôme">
              <input type="text" value={profil?.diplome || ''} onChange={(e) => setField('diplome', e.target.value)}
                placeholder="Ex : Master en Informatique"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </Field>
          </div>

          {message && (
            <div className={`text-xs rounded p-2 flex items-start gap-2 ${
              message.type === 'success'
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
            }`}>
              {message.type === 'success' ? <CheckCircle2 size={13} className="flex-shrink-0 mt-0.5" /> : <X size={13} className="flex-shrink-0 mt-0.5" />}
              <span>{message.text}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => setExpanded(false)}
              className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
              Fermer
            </button>
            <button type="submit" disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold rounded transition-colors">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value, className = '' }: { icon: React.ReactNode; label: string; value: string; className?: string }) {
  return (
    <div className={`flex items-center gap-2 text-xs ${className}`}>
      <span className="text-gray-400 flex-shrink-0">{icon}</span>
      <span className="text-gray-500 dark:text-gray-400 font-medium">{label} :</span>
      <span className="text-gray-900 dark:text-gray-100 truncate">{value}</span>
    </div>
  );
}

function Field({ label, icon, children, className = '' }: { label: string; icon?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5">
        {icon} {label}
      </label>
      {children}
    </div>
  );
}

/**
 * Carte "Changer mon mot de passe" — accessible à tout moment depuis le
 * portail formateur pour raisons de sécurité (le formateur ne dépend pas
 * de la scolarité pour renouveler son mot de passe).
 */
function ChangePasswordCard() {
  const [ancien, setAncien] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showAncien, setShowAncien] = useState(false);
  const [showNouveau, setShowNouveau] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (!ancien || !nouveau) {
      setMessage({ type: 'error', text: 'Renseignez l\'ancien et le nouveau mot de passe.' });
      return;
    }
    if (nouveau.length < 8) {
      setMessage({ type: 'error', text: 'Le nouveau mot de passe doit contenir au moins 8 caractères.' });
      return;
    }
    if (nouveau !== confirm) {
      setMessage({ type: 'error', text: 'Les deux nouveaux mots de passe ne correspondent pas.' });
      return;
    }
    if (nouveau === ancien) {
      setMessage({ type: 'error', text: 'Le nouveau mot de passe doit être différent de l\'ancien.' });
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
      const resp = await fetch('/api/portail-formateur/me/change-password/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ancien_password: ancien, nouveau_password: nouveau }),
      });
      const data = await resp.json().catch(() => ({} as any));
      if (!resp.ok) {
        setMessage({ type: 'error', text: data.detail || 'Erreur lors du changement.' });
      } else {
        setMessage({ type: 'success', text: data.message || 'Mot de passe modifié avec succès.' });
        setAncien(''); setNouveau(''); setConfirm('');
      }
    } catch {
      setMessage({ type: 'error', text: 'Erreur réseau — réessayez.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-1">
        <KeyRound size={18} className="text-blue-600 dark:text-blue-400" />
        Sécurité — changer mon mot de passe
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Renouvelez votre mot de passe à tout moment. Minimum 8 caractères.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ancien mot de passe *
          </label>
          <div className="relative">
            <input
              type={showAncien ? 'text' : 'password'}
              value={ancien}
              onChange={(e) => setAncien(e.target.value)}
              autoComplete="current-password"
              className="w-full pr-9 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <button
              type="button"
              onClick={() => setShowAncien(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showAncien ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nouveau mot de passe *
          </label>
          <div className="relative">
            <input
              type={showNouveau ? 'text' : 'password'}
              value={nouveau}
              onChange={(e) => setNouveau(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              className="w-full pr-9 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <button
              type="button"
              onClick={() => setShowNouveau(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showNouveau ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Confirmer le nouveau mot de passe *
          </label>
          <input
            type={showNouveau ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        {message && (
          <div className={`text-xs rounded p-2 flex items-start gap-2 ${
            message.type === 'success'
              ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
          }`}>
            {message.type === 'success' ? <ShieldCheck size={13} className="flex-shrink-0 mt-0.5" /> : <X size={13} className="flex-shrink-0 mt-0.5" />}
            <span>{message.text}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !ancien || !nouveau || !confirm}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
          {loading ? 'Modification en cours…' : 'Modifier mon mot de passe'}
        </button>
      </form>
    </div>
  );
}

function PushCard() {
  const { supported, permission, enabled, loading, enable, disable } = usePushFormateur();
  const [test, { isLoading: testing }] = usePushTestFormateurMutation();
  const [msg, setMsg] = useState('');

  const onToggle = async () => {
    setMsg('');
    if (enabled) {
      await disable();
      setMsg('Notifications désactivées sur cet appareil.');
    } else {
      const ok = await enable();
      setMsg(ok
        ? 'Notifications activées.'
        : permission === 'denied'
          ? 'Permission refusée. Allez dans les réglages du navigateur pour réautoriser.'
          : 'Activation impossible.');
    }
  };

  const onTest = async () => {
    setMsg('');
    try {
      const r = await test().unwrap();
      setMsg(r.envoyees > 0
        ? `Test envoyé (${r.envoyees} appareil(s)).`
        : "Aucun appareil enregistré — activez d'abord les notifications.");
    } catch {
      setMsg("Échec d'envoi du test.");
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-3">
      <h2 className="text-[11px] uppercase tracking-wider text-gray-400 font-bold mb-2">
        Notifications push
      </h2>
      {!supported ? (
        <div className="text-xs text-gray-500">
          Non supporté sur ce navigateur. Installez l'app (PWA) sur votre téléphone.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-gray-300">
              {enabled
                ? 'Activées sur cet appareil — vous recevrez les rappels.'
                : 'Désactivées. Activez pour recevoir les rappels de dépôt et messages.'}
            </div>
            <button
              onClick={onToggle} disabled={loading || permission === 'denied'}
              className={`px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 shrink-0
                ${enabled
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                  : 'bg-amber-600 hover:bg-amber-700 text-white'}`}
            >
              {loading
                ? <Loader2 size={12} className="animate-spin" />
                : enabled ? <BellOff size={12} /> : <Bell size={12} />}
              {enabled ? 'Désactiver' : 'Activer'}
            </button>
          </div>
          {enabled && (
            <button
              onClick={onTest} disabled={testing}
              className="w-full py-1.5 text-[11px] text-gray-400 hover:text-amber-400 border border-gray-700 hover:border-amber-700 rounded flex items-center justify-center gap-1.5"
            >
              {testing ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
              Envoyer une notification de test
            </button>
          )}
          {permission === 'denied' && (
            <div className="text-[11px] text-amber-300 bg-amber-900/20 rounded p-2">
              Le navigateur a bloqué les notifications. Réautorisez-les depuis les réglages du site.
            </div>
          )}
          {msg && (
            <div className="text-[11px] text-gray-300 bg-gray-800 rounded p-2">{msg}</div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, v }: { label: string; v: number }) {
  return (
    <div className="bg-gray-800/50 rounded-lg p-2">
      <div className="text-lg font-bold text-white">{v}</div>
      <div className="text-[9px] uppercase text-gray-400">{label}</div>
    </div>
  );
}
